const LABOR_AMEND_ENCOUNTER_SAVED_PREFIX = "labor-amend-encounter-saved:";
const LABOR_AMEND_POST_EFFECTS_PENDING_PREFIX = "labor-amend-post-effects-pending:";
const LABOR_AMEND_PENDING_WORK_PREFIX = "labor-amend-pending-work:";
const LABOR_AMEND_FLOWSHEET_PARTIAL_COMMIT_PREFIX = "labor-amend-flowsheet-partial-commit:";

function sessionKey(prefix: string, encounterId: string): string {
  return `${prefix}${encounterId}`;
}

function isSessionStorageUnavailable(): boolean {
  return typeof sessionStorage === "undefined";
}

/** Reads a session flag; returns false when storage is missing or throws (e.g. Safari private mode). */
function readFlag(prefix: string, encounterId: string): boolean {
  if (isSessionStorageUnavailable()) {
    return false;
  }
  try {
    return sessionStorage.getItem(sessionKey(prefix, encounterId)) === "1";
  } catch {
    return false;
  }
}

/** Writes a session flag; no-ops when storage is missing or throws. */
function writeFlag(prefix: string, encounterId: string): void {
  if (isSessionStorageUnavailable()) {
    return;
  }
  try {
    sessionStorage.setItem(sessionKey(prefix, encounterId), "1");
  } catch {
    // Fail closed: amend refresh markers are best-effort only.
  }
}

/** Clears a session flag; no-ops when storage is missing or throws. */
function clearFlag(prefix: string, encounterId: string): void {
  if (isSessionStorageUnavailable()) {
    return;
  }
  try {
    sessionStorage.removeItem(sessionKey(prefix, encounterId));
  } catch {
    // Fail closed: amend refresh markers are best-effort only.
  }
}

/** Whether encounter QR amendment was saved this browser session (survives page reload). */
export function readLaborAmendEncounterSavedFromSession(encounterId: string): boolean {
  return readFlag(LABOR_AMEND_ENCOUNTER_SAVED_PREFIX, encounterId);
}

export function writeLaborAmendEncounterSavedToSession(encounterId: string): void {
  writeFlag(LABOR_AMEND_ENCOUNTER_SAVED_PREFIX, encounterId);
}

/** Encounter saved but post-amendment side effects have not completed yet. */
export function readLaborAmendPostEffectsPendingFromSession(encounterId: string): boolean {
  return readFlag(LABOR_AMEND_POST_EFFECTS_PENDING_PREFIX, encounterId);
}

export function writeLaborAmendPostEffectsPendingToSession(encounterId: string): void {
  writeFlag(LABOR_AMEND_POST_EFFECTS_PENDING_PREFIX, encounterId);
}

export function clearLaborAmendPostEffectsPendingFromSession(encounterId: string): void {
  clearFlag(LABOR_AMEND_POST_EFFECTS_PENDING_PREFIX, encounterId);
}

/** Unflushed amend queue or preview state may exist for this encounter. */
export function readLaborAmendPendingWorkFromSession(encounterId: string): boolean {
  return readFlag(LABOR_AMEND_PENDING_WORK_PREFIX, encounterId);
}

export function writeLaborAmendPendingWorkToSession(encounterId: string): void {
  writeFlag(LABOR_AMEND_PENDING_WORK_PREFIX, encounterId);
}

export function clearLaborAmendPendingWorkFromSession(encounterId: string): void {
  clearFlag(LABOR_AMEND_PENDING_WORK_PREFIX, encounterId);
}

/** At least one deferred flowsheet job was written to Medplum before Sign & Lock finished. */
export function readLaborAmendFlowsheetPartialCommitFromSession(encounterId: string): boolean {
  return readFlag(LABOR_AMEND_FLOWSHEET_PARTIAL_COMMIT_PREFIX, encounterId);
}

export function writeLaborAmendFlowsheetPartialCommitToSession(encounterId: string): void {
  writeFlag(LABOR_AMEND_FLOWSHEET_PARTIAL_COMMIT_PREFIX, encounterId);
}

export function clearLaborAmendFlowsheetPartialCommitFromSession(encounterId: string): void {
  clearFlag(LABOR_AMEND_FLOWSHEET_PARTIAL_COMMIT_PREFIX, encounterId);
}

export function clearLaborAmendEncounterSavedFromSession(encounterId: string): void {
  clearFlag(LABOR_AMEND_ENCOUNTER_SAVED_PREFIX, encounterId);
}

/** Clears all labor-amend session markers for an encounter. */
export function clearAllLaborAmendSessionState(encounterId: string): void {
  clearLaborAmendEncounterSavedFromSession(encounterId);
  clearLaborAmendPostEffectsPendingFromSession(encounterId);
  clearLaborAmendPendingWorkFromSession(encounterId);
  clearLaborAmendFlowsheetPartialCommitFromSession(encounterId);
}
